import React, { useState } from 'react';
import { AnalyzeBusinessRequest, LLMProvider } from '../types';
import { analyzeBusiness } from '../services/api';
import { businessProfiles, BusinessProfile } from '../data/businessProfiles';
import './BusinessAnalysisForm.css';

interface BusinessAnalysisFormProps {
  onAnalysisComplete: (data: any) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const BusinessAnalysisForm: React.FC<BusinessAnalysisFormProps> = ({
  onAnalysisComplete,
  loading,
  setLoading,
}) => {
  const [formData, setFormData] = useState<AnalyzeBusinessRequest>({
    businessLexicon: '',
    category: '',
    risks: [],
    trends: [],
    llmProvider: 'openai',
  });

  const [riskInput, setRiskInput] = useState('');
  const [trendInput, setTrendInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<string>('');

  const handleProfileSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const profileId = e.target.value;
    setSelectedProfile(profileId);

    if (profileId) {
      const profile = businessProfiles.find((p) => p.id === profileId);
      if (profile) {
        setFormData({
          businessLexicon: profile.businessLexicon,
          category: profile.category,
          risks: [...profile.risks],
          trends: [...profile.trends],
          llmProvider: formData.llmProvider, // Keep the selected LLM provider
        });
      }
    } else {
      // Clear form if "Custom" is selected
      setFormData({
        businessLexicon: '',
        category: '',
        risks: [],
        trends: [],
        llmProvider: formData.llmProvider,
      });
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddRisk = () => {
    if (riskInput.trim() && !formData.risks.includes(riskInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        risks: [...prev.risks, riskInput.trim()],
      }));
      setRiskInput('');
    }
  };

  const handleRemoveRisk = (risk: string) => {
    setFormData((prev) => ({
      ...prev,
      risks: prev.risks.filter((r) => r !== risk),
    }));
  };

  const handleAddTrend = () => {
    if (trendInput.trim() && !formData.trends.includes(trendInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        trends: [...prev.trends, trendInput.trim()],
      }));
      setTrendInput('');
    }
  };

  const handleRemoveTrend = (trend: string) => {
    setFormData((prev) => ({
      ...prev,
      trends: prev.trends.filter((t) => t !== trend),
    }));
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    type: 'risk' | 'trend'
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (type === 'risk') {
        handleAddRisk();
      } else {
        handleAddTrend();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.businessLexicon.trim()) {
      setError('Please enter your business lexicon');
      return;
    }

    if (!formData.category.trim()) {
      setError('Please enter a category');
      return;
    }

    if (formData.risks.length === 0) {
      setError('Please add at least one risk to analyze');
      return;
    }

    if (formData.trends.length === 0) {
      setError('Please add at least one trend to analyze');
      return;
    }

    setLoading(true);
    try {
      const response = await analyzeBusiness(formData);
      onAnalysisComplete(response);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to analyze business. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <h2>Business Analysis Request</h2>
      <p className="instruction-text">
        Enter your business information and select the risks and trends you want to analyze.
      </p>

      {error && <div className="error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="businessProfile">Quick Start - Select a Business Profile</label>
          <select
            id="businessProfile"
            value={selectedProfile}
            onChange={handleProfileSelect}
            className="profile-select"
          >
            <option value="">-- Select a business profile or enter custom --</option>
            {businessProfiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name} ({profile.primarySector} - {profile.subSector})
              </option>
            ))}
          </select>
          {selectedProfile && (
            <p className="profile-info">
              Selected: {businessProfiles.find((p) => p.id === selectedProfile)?.name}
            </p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="businessLexicon">Business Lexicon *</label>
          <textarea
            id="businessLexicon"
            name="businessLexicon"
            value={formData.businessLexicon}
            onChange={handleInputChange}
            placeholder="Describe your business, industry, and key terminology..."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">Category *</label>
          <input
            type="text"
            id="category"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            placeholder="e.g., Technology, Healthcare, Finance..."
            required
          />
        </div>

        <div className="form-group">
          <label>Risks to Analyze *</label>
          <div className="tags-input">
            {formData.risks.map((risk) => (
              <span key={risk} className="tag">
                {risk}
                <button
                  type="button"
                  onClick={() => handleRemoveRisk(risk)}
                  aria-label={`Remove ${risk}`}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              type="text"
              value={riskInput}
              onChange={(e) => setRiskInput(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'risk')}
              placeholder="Add a risk and press Enter"
            />
          </div>
          <button
            type="button"
            onClick={handleAddRisk}
            className="add-button"
            disabled={!riskInput.trim()}
          >
            Add Risk
          </button>
        </div>

        <div className="form-group">
          <label>Trends to Analyze *</label>
          <div className="tags-input">
            {formData.trends.map((trend) => (
              <span key={trend} className="tag">
                {trend}
                <button
                  type="button"
                  onClick={() => handleRemoveTrend(trend)}
                  aria-label={`Remove ${trend}`}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              type="text"
              value={trendInput}
              onChange={(e) => setTrendInput(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'trend')}
              placeholder="Add a trend and press Enter"
            />
          </div>
          <button
            type="button"
            onClick={handleAddTrend}
            className="add-button"
            disabled={!trendInput.trim()}
          >
            Add Trend
          </button>
        </div>

        <div className="form-group">
          <label>LLM Provider *</label>
          <div className="llm-selector">
            <div
              className={`llm-option ${formData.llmProvider === 'openai' ? 'selected' : ''}`}
              onClick={() =>
                setFormData((prev) => ({ ...prev, llmProvider: 'openai' }))
              }
            >
              <h4>OpenAI</h4>
              <p>GPT-4 with real-time search</p>
            </div>
            <div
              className={`llm-option ${formData.llmProvider === 'perplexity' ? 'selected' : ''}`}
              onClick={() =>
                setFormData((prev) => ({ ...prev, llmProvider: 'perplexity' }))
              }
            >
              <h4>Perplexity</h4>
              <p>Sonar with online search</p>
            </div>
          </div>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Analyzing...' : 'Analyze Business'}
        </button>
      </form>

      {loading && (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Analyzing your business data with {formData.llmProvider}...</p>
        </div>
      )}
    </div>
  );
};

export default BusinessAnalysisForm;

